import { IconArrowUp } from "@tabler/icons-react";
import React from "react";
import { useTranslation } from "react-i18next";
import TextareaAutosize from "react-textarea-autosize";
import FilterIcon from "@/assets/icons/filter.svg?react";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { cn } from "@/lib/utils";
import { initialConversationState, useChatStore } from "@/stores/ui/useChatStore";

const MAX_CONTENT_LENGTH = 5000;

interface ChatInputProps {
  onSubmit: () => void;
  disabled?: boolean;
  onToggleFilter: () => void;
  isFilterOpen: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSubmit,
  disabled = false,
  onToggleFilter,
  isFilterOpen,
}) => {
  const { t } = useTranslation("voiceek_ui");
  const setCurrentConversationState = useChatStore((s) => s.setCurrentConversationState);
  const { content } = useChatStore((s) => {
    const state = s.conversationStates[s.currentConversationId];

    return state || initialConversationState;
  });

  const isContentTooLong = content.length > MAX_CONTENT_LENGTH;
  const isSubmitDisabled = disabled || !content.trim() || isContentTooLong;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const composing = e.nativeEvent.isComposing;
    const hotkey = e.key === "Enter" && e.shiftKey;

    if (!composing && hotkey) {
      if (!disabled && content.trim() && !isContentTooLong) {
        e.preventDefault();
        onSubmit();
      }
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          "flex-1 bg-white border rounded-sm w-full mt-1 pl-4 pb-2 flex flex-col gap-4",
          "border-[#6788E1]",
          isContentTooLong
            ? "shadow-[0_0_10px_0_#293D7266,0_1px_4px_0_inset_#6788E1E5]"
            : "focus-within:shadow-[0_0_10px_0_#293D7266]",
        )}
      >
        <ScrollArea viewportClassName="max-h-32">
          <TextareaAutosize
            value={content}
            onChange={(e) => {
              setCurrentConversationState({ content: e.target.value });
            }}
            onKeyDown={handleKeyDown}
            minRows={2}
            placeholder={t("chat.modal.placeholder")}
            data-testid="chat-input"
            className={cn(
              "w-full overflow-hidden pt-4 pr-4 resize-none placeholder:text-[#B7B7B7] caret-[#6888E1]",
            )}
          />
          <ScrollBar className="pt-4 pr-1" />
        </ScrollArea>
        <div className="flex items-center pr-4 justify-between">
          <button
            onClick={onToggleFilter}
            className={cn(
              "flex pl-1 pr-2 h-[34px] items-center hover:bg-gray-100 rounded hover:cursor-pointer",
              isFilterOpen && "bg-[#E1EAFF]",
            )}
            aria-label={t("chat.filter.toggle")}
            data-testid="chat-filter-button"
          >
            <FilterIcon className="h-[26px] w-[26px] fill-[#6888E1] " />
            <span className="text-[#6888E1] font-semibold">{t("chat.filter.title")}</span>
          </button>
          <button
            type="submit"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            data-testid="chat-send-button"
            className={cn(
              "flex items-center justify-center px-4 py-2 space-x-1 font-medium rounded-full transition-colors",
              isSubmitDisabled
                ? "bg-[#E0E0E0] text-[#5F6368] hover:cursor-not-allowed"
                : "bg-[#6888E1] text-white hover:bg-[#5677D0] hover:cursor-pointer",
            )}
          >
            <span>Shift+Enter</span>
            <IconArrowUp className="h-4 w-auto" />
          </button>
        </div>
      </div>
      {isContentTooLong && (
        <p className="text-[#6888E1] text-xs font-semibold text-center leading-[1.4]">
          メッセージが長すぎます。{MAX_CONTENT_LENGTH}文字以内で入力してください。
        </p>
      )}
    </div>
  );
};
